import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import filter from 'lodash/filter';
import map from 'lodash/map';
import slice from 'lodash/slice';
import { isFileAttachment, isMediaAttachment, isScrapedContent, isUploadedImage } from 'stream-chat-react';

import { LinkAttachment } from './link-attachment';
import { ImageAttachment } from './image-attachment';
import { FileAttachment } from './file-attachment';
import { StreamModalGallery } from './stream-modal-gallery';
import { VideoAttachment } from './video-attachment';
import { ExpandAttachments } from './expand-attachments';
import { useWindowSize } from '../../../shared-components/utils/hooks/use-window-size';

const ATTACHMENTS_LENGTH_TO_HIDE = 4;

export const AttachmentList = ({ attachments }) => {
  const [openGallery, setOpenGallery] = useState(false);
  const [galleryIndex, setGalleryIndex] = useState(0);
  const [isExpanded, setIsExpanded] = useState(false);

  const toggleIsExpanded = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, [isExpanded]);

  const [scrapedAttachments, hasScrapedAttachments] = useMemo(() => {
    const list = filter(attachments, isScrapedContent);
    return [list, 0 < list.length];
  }, [attachments]);

  const [images, videos, files, hasAttachments] = useMemo(() => {
    const allowedList = filter(attachments, (attachment) => !isScrapedContent(attachment));
    const images = filter(allowedList, isUploadedImage);
    const videos = filter(allowedList, isMediaAttachment);
    const files = filter(allowedList, (attachment) => {
      return isFileAttachment(attachment) && 'image' !== attachment.type;
    });
    const list = [images, videos, files];
    return [...list, 0 < list.length];
  }, [attachments]);

  const openModal = useCallback((index = 0) => {
    setGalleryIndex(index);
    setOpenGallery(true);
  }, [open]);

  const attachmentList = useMemo(() => [...images, ...videos, ...files], [images, videos, files]);
  const mediaList = useMemo(() => [...images, ...videos], [images, videos]);

  const { width: screenWidth } = useWindowSize();
  const shouldExpand = useMemo(() => {
    return 768 > screenWidth && ATTACHMENTS_LENGTH_TO_HIDE < attachmentList.length;
  }, [screenWidth, attachmentList.length]);

  const displayAttachmentList = useMemo(() => {
    if (shouldExpand) {
      const start = 0;
      const end = isExpanded ? attachmentList.length : ATTACHMENTS_LENGTH_TO_HIDE - 1;
      return slice(attachmentList, start, end);
    }
    return attachmentList;
  }, [attachmentList, isExpanded, shouldExpand]);

  return (
    <>
      {0 < mediaList.length
        && <StreamModalGallery
          items={mediaList}
          index={galleryIndex}
          setIndex={setGalleryIndex}
          setOpen={setOpenGallery}
          open={openGallery}
        />
      }
      {hasScrapedAttachments
        && <div className="AttachmentList">
          {map(scrapedAttachments, (attachment) => <LinkAttachment {...attachment} />)}
        </div>
      }
      {hasAttachments
        && <div className="AttachmentList">
          {map(displayAttachmentList, (attachment, index) => {
            if (isUploadedImage(attachment)) {
              const onClick = () => openModal(index);
              return <ImageAttachment {...attachment} onClick={onClick} />;
            }
            if (isMediaAttachment(attachment)) {
              const onClick = () => openModal(index);
              return <VideoAttachment {...attachment} onClick={onClick} />;
            }
            if (isFileAttachment(attachment)) {
              return <FileAttachment {...attachment} />;
            }
          })}
          {shouldExpand && <ExpandAttachments isExpanded={isExpanded} onClick={toggleIsExpanded} />}
        </div>
      }
    </>
  );
};

AttachmentList.propTypes = {
  attachments: PropTypes.array
};
